import React from 'react';
import { styled } from '@compiled/react';
import { isNewDisclaimerForAIAnswersEnabled } from 'feature-flags';
import { useIntl } from 'react-intl-next';
import Icon from '@atlaskit/icon';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import { Inline } from '@atlaskit/primitives';
import { N200, B400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { HtmlAnchor } from '@atlassian/help-center-common-component/analytics/html-anchor';
import { Link } from '@atlassian/help-center-common-component/analytics/link';
import { AtlassianIntelligenceIcon } from '../../icons/AtlassianIntelligenceIcon';
import messages from './messages';

export const DisclaimerFooter = () => {
    const { formatMessage } = useIntl();

    return (
        <>
            {isNewDisclaimerForAIAnswersEnabled() ? (
                <Inline spread="space-between" alignInline="center">
                    <DisclaimerText>
                        <Tooltip content={formatMessage(messages.footerDisclaimerTooltipText)}>
                            <EditorPanelIcon label="Additional Disclaimer" />
                        </Tooltip>
                        <FooterTextLink
                            className="cv-exclude-from-customisation"
                            href="https://www.atlassian.com/trust/atlassian-intelligence"
                            target="_blank"
                            rel="noopener noreferrer"
                            actionSubjectId="aiAnswersDisclaimer"
                        >
                            {formatMessage(messages.footerContentQualityDisclaimerText)}
                        </FooterTextLink>
                        {formatMessage(messages.footerConversationStoredDisclaimerText)}
                    </DisclaimerText>
                    <PoweredByAtlassianIntelligence>
                        {formatMessage(messages.poweredByAtlassianIntelligence)}
                        <Icon glyph={AtlassianIntelligenceIcon} label="Atlassian Intelligence Icon" />
                    </PoweredByAtlassianIntelligence>
                </Inline>
            ) : (
                <Disclaimer>
                    {formatMessage(messages.disclaimerText)}&nbsp;
                    <FooterLink
                        href="https://www.atlassian.com/trust/atlassian-intelligence"
                        target="_blank"
                        rel="noopener noreferrer"
                        actionSubjectId="learnWhyLink"
                    >
                        {formatMessage(messages.learnWhyLink)}
                    </FooterLink>
                </Disclaimer>
            )}
        </>
    );
};

const Disclaimer = styled.p({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '11px',
    lineHeight: '14px',
    color: token('color.text.subtlest', N200),
    marginTop: token('space.100', '8px'),
    flexWrap: 'wrap',
    textAlign: 'center',
});

const FooterLink = styled(HtmlAnchor)({
    color: token('color.link', B400),
    textDecoration: 'none',
    ':hover': {
        textDecoration: 'none',
    },
});

const DisclaimerText = styled.p({
    display: 'flex',
    fontSize: '11px',
    lineHeight: '14px',
    color: token('color.text.subtlest', N200),
    alignItems: 'center',
    flexWrap: 'wrap',
});

const PoweredByAtlassianIntelligence = styled.p({
    display: 'flex',
    fontSize: '11px',
    lineHeight: '14px',
    color: token('color.text.subtlest', N200),
    alignItems: 'center',
    margin: 0,
});

const FooterTextLink = styled(Link)({
    color: token('color.text.subtle', N200),
    overflowWrap: 'break-word',
    textDecoration: 'none',
    marginRight: token('space.025', '2px'),
    ':hover': {
        color: token('color.text.subtle', N200),
        textDecoration: 'none',
        marginRight: token('space.025', '2px'),
    },
});
